import React, { useEffect, useMemo } from 'react';
import { CheckCircle2, Power, Activity } from 'lucide-react';
import { formatMWK } from './tokens';

/**
 * Phase 4 — Daily goal celebration.
 * Triggered once when daily progress crosses 100%. Subtle animated card +
 * pure-CSS confetti (no extra deps). Auto-dismisses after 6s if no action.
 *
 * Props:
 *   earned, goal — current totals
 *   onGoOffline()
 *   onKeepEarning()
 */
const DailyGoalCelebration = ({ earned, goal, onGoOffline, onKeepEarning }) => {
  useEffect(() => {
    const t = setTimeout(() => onKeepEarning?.(), 6000);
    return () => clearTimeout(t);
  }, [onKeepEarning]);

  // Generate confetti pieces once
  const pieces = useMemo(() => {
    const colors = ['#16A34A', '#F59E0B', '#E11D48', '#3B82F6', '#A855F7', '#10B981'];
    return Array.from({ length: 28 }).map((_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 0.8,
      duration: 1.6 + Math.random() * 1.4,
      rotate: Math.random() * 720,
      color: colors[i % colors.length],
      size: 6 + Math.random() * 6,
    }));
  }, []);

  return (
    <div className="fixed inset-0 z-[82] pointer-events-none" data-testid="goal-celebration">
      {/* Confetti — pointer-events:none so they don't block interaction */}
      <div className="absolute inset-0 overflow-hidden">
        {pieces.map((p) => (
          <span
            key={p.id}
            className="absolute top-0 will-change-transform"
            style={{
              left: `${p.left}%`,
              width: `${p.size}px`,
              height: `${p.size * 0.4}px`,
              backgroundColor: p.color,
              transform: `translateY(-20px) rotate(${p.rotate}deg)`,
              animation: `confetti-fall ${p.duration}s cubic-bezier(0.4, 0.0, 1, 1) ${p.delay}s forwards`,
              borderRadius: '2px',
            }}
          />
        ))}
      </div>

      {/* Card (re-enables pointer events) */}
      <div className="absolute left-3 right-3 pointer-events-auto" style={{ top: 'calc(80px + env(safe-area-inset-top, 0))' }}>
        <div
          className="bg-white rounded-3xl shadow-[0_16px_40px_rgba(0,0,0,0.18)] border border-emerald-200 p-5 animate-slide-up"
          data-testid="goal-celebration-card"
        >
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center ring-2 ring-emerald-50">
              <CheckCircle2 className="w-7 h-7" strokeWidth={2.5} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] uppercase tracking-wider font-semibold text-emerald-700">Goal reached!</p>
              <p className="text-[16px] font-bold text-gray-900 tabular-nums">
                {formatMWK(earned || 0)} earned today 🎯
              </p>
              <p className="text-[11px] text-gray-500">
                Beat your {formatMWK(goal || 0)} goal · keep the streak going?
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-3">
            <button
              type="button"
              onClick={onGoOffline}
              data-testid="goal-celebration-offline"
              className="h-11 rounded-2xl bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold text-[12px] inline-flex items-center justify-center gap-1.5"
            >
              <Power className="w-3.5 h-3.5" /> Go offline
            </button>
            <button
              type="button"
              onClick={onKeepEarning}
              data-testid="goal-celebration-keep"
              className="h-11 rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-[12px] inline-flex items-center justify-center gap-1.5"
            >
              <Activity className="w-3.5 h-3.5" /> Keep earning
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DailyGoalCelebration;
